import { Stack } from '@mui/material';
import { difference, isEmpty } from 'lodash';
import { useEffect } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { AssetProviderUnlinkWarning } from '@/modules/assetProviderIntegrations/shared/AssetProviderUnlinkWarning';
import {
  useAssetProviderDataLinkingState,
  useAssetProviderDisplayName,
} from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { IntegrationClientsTypeahead } from '@/modules/assetProviderIntegrations/shared/IntegrationClientTypeahead/IntegrationClientsTypeahead';
import { IntegrationInputCard } from '@/modules/assetProviderIntegrations/shared/IntegrationInputCard';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { PathsOf } from '@/types/subform';

import { useUpdateHouseholdIntegrationClientsMutation } from '../graphql/ManageHouseholdAccessForm.generated';

export interface IntegrationClientFormProps {
  initialIntegrationClientIds?: string[];
  householdId: string;
}

interface IntegrationClientFormShape {
  integrationClientIds: string[];
  /** used to calculate the diff in the integration client IDs */
  _initialIntegrationClientIds: string[];
}

export type IntegrationClientFormPaths = PathsOf<IntegrationClientFormShape>;

/**
 * @description IntegrationClientForm is responsible for connecting the household to one or more integration clients.
 */
export function IntegrationClientForm({
  initialIntegrationClientIds,
  householdId,
}: IntegrationClientFormProps) {
  const canManageIntegrationData = useCanManageIntegrationData();
  const assetProviderNameInline = useAssetProviderDisplayName({
    isInline: true,
  });
  const assetProviderName = useAssetProviderDisplayName({ isInline: false });
  const { canConnectToIntegrations, integrationsAvailableForDataLinking } =
    useAssetProviderDataLinkingState();
  const { createErrorFeedback, createSuccessFeedback } = useFeedback();
  const [updateHousehold, { loading }] =
    useUpdateHouseholdIntegrationClientsMutation({
      onError: createErrorFeedback(
        'Failed to update client with integration data. Please refresh the page and try again.'
      ),
      onCompleted: createSuccessFeedback('Client updates saved successfully'),
    });
  const formMethods = useForm<IntegrationClientFormShape>({
    defaultValues: {
      integrationClientIds: [],
    },
  });
  const { control, handleSubmit, reset, watch, getFieldState } = formMethods;

  const selectedIntegrationClientIds = watch('integrationClientIds');
  const isFieldChanged = getFieldState('integrationClientIds').isDirty;

  // we do this here instead of passing directly into defaultValues because this value is driven via the API
  // we won't have it at the time we instantiate the form
  useEffect(
    function syncInitialValuesToForm() {
      if (!initialIntegrationClientIds) return;
      reset({
        integrationClientIds: initialIntegrationClientIds,
        _initialIntegrationClientIds: initialIntegrationClientIds,
      });
    },
    [initialIntegrationClientIds, reset]
  );

  const onSubmit = handleSubmit((data) => {
    const addIntegrationClientIDs = difference(
      data.integrationClientIds,
      data._initialIntegrationClientIds
    );
    const removeIntegrationClientIDs = difference(
      data._initialIntegrationClientIds,
      data.integrationClientIds
    );

    return updateHousehold({
      variables: {
        householdId,
        input: {
          ...(addIntegrationClientIDs.length > 0 && {
            addIntegrationClientIDs,
          }),
          ...(removeIntegrationClientIDs.length > 0 && {
            removeIntegrationClientIDs,
          }),
        },
      },
    });
  });

  if (canConnectToIntegrations.length === 0 || !canManageIntegrationData) {
    return null;
  }

  const hasIntegrationDataAccess = canConnectToIntegrations.some(
    (integration) => integrationsAvailableForDataLinking.includes(integration)
  );

  return (
    <IntegrationInputCard providerName={assetProviderName!} variant="outlined">
      <FormProvider {...formMethods}>
        <Stack spacing={2} onSubmit={onSubmit} component="form">
          <IntegrationClientsTypeahead
            providers={canConnectToIntegrations}
            control={control}
            notLoggedIn={!hasIntegrationDataAccess}
            fieldName={
              `integrationClientIds` as const satisfies IntegrationClientFormPaths
            }
            label={`${assetProviderNameInline} client`}
            hideLabel
          />
          {isFieldChanged && isEmpty(selectedIntegrationClientIds) && (
            <AssetProviderUnlinkWarning />
          )}
          <Button
            disabled={!hasIntegrationDataAccess}
            loading={loading}
            type="submit"
            variant="primary"
            size="sm"
          >
            Save
          </Button>
        </Stack>
      </FormProvider>
    </IntegrationInputCard>
  );
}
